import React, { useEffect, useState } from "react"
import AsyncSelect from "react-select/async"
import { get } from "helpers/api_helper"
import modalSelectMenuProps from "components/Common/modalSelectMenuProps"
import DateTimeField from "components/Common/DateTimeField"
import { BALLS_TBD } from "pages/EventsManagement/eventCreationForm"
import { COUNTRIES, countryTimezone, seasonOfDay } from "./plannerQuery"
import { clubCountry } from "./proposalForm"

// A value's source pill, beside its label (B3 pinned decision Q9).
export const SourcePill = ({ source }) => (source
  ? <span className={"ep-source" + (source.kind === "edited" ? " is-edited" : "")} title={source.title}>{source.label}</span> : null)

export function Field({ id, label, required, source, error, help, children, wide }) {
  return <div className={"ep-field" + (wide ? " is-wide" : "")}>
    <div className="ep-field-head">
      <label htmlFor={id} className={required ? "is-required" : undefined}>{label}</label>
      <SourcePill source={source} />
    </div>
    {children}
    {help && <small className="ep-meta">{help}</small>}
    {error && <small className="text-danger" id={id + "-error"}>{error}</small>}
  </div>
}

const clubLabel = club => (!club ? "" : club.kind === "tba" ? "TBA (to be announced)" : club.kind === "none" ? "No club"
  : [club.clubName, club.location?.city].filter(Boolean).join(" · "))
const TBA = { kind: "tba" }, NO_CLUB = { kind: "none" }
// The drawer's body scrolls: a list opens in the page's top layer, so it is never clipped (CP lesson 2026-09-21); dialogs and pages of the
// planner use it too.
export const portalMenu = { ...modalSelectMenuProps, menuPortalTarget: document.body }
// A club of the platform (activated), TBA or No club, as event creation offers them (CreateEventChoices).
function ClubField({ id, club, disabled, onChange }) {
  const [failure, setFailure] = useState("")
  const load = async input => {
    setFailure("")
    const fixed = [{ label: clubLabel(TBA), value: TBA }, { label: clubLabel(NO_CLUB), value: NO_CLUB }]
    if (input.trim().length < 2) return fixed
    try {
      const response = await get("/clubs?" + new URLSearchParams({ status: "activated", search: input.trim(), page: 1, limit: 25 }))
      if (!Array.isArray(response?.data?.results)) throw new Error("Invalid response")
      return [...fixed, ...response.data.results.map(found => {
        const value = { kind: "club", id: found.id || found._id, clubName: found.clubName, location: found.location }
        return { label: clubLabel(value), value }
      })]
    } catch (error) {
      setFailure("Could not load clubs. Type again to retry.")
      return fixed
    }
  }
  return <>
    <AsyncSelect inputId={id} classNamePrefix="ep-select" {...portalMenu} cacheOptions={false} defaultOptions loadOptions={load} isDisabled={disabled}
      value={club ? { label: clubLabel(club), value: club } : null} placeholder="Search a club, or choose TBA / No club"
      onChange={option => onChange(option?.value || null)} noOptionsMessage={() => "Type at least two letters of the club's name"} />
    {failure && <small className="text-danger">{failure}</small>}
  </>
}

const accountName = account => {
  const info = account.organizerInfo || account
  return info.clientType === "company" ? info.companyName : [info.firstName || account.firstName, info.lastName || account.lastName].filter(Boolean).join(" ")
}
// Any activated organizer, searched by name (TBA or No club in the editor; a generation by organizer).
export function OrganizerSearch({ id, organizer, disabled, onChange }) {
  const [failure, setFailure] = useState("")
  const load = async input => {
    setFailure("")
    if (input.trim().length < 2) return []
    try {
      const response = await get("/admin/organizers?" + new URLSearchParams({ status: "activated", search: input.trim(), page: 1, limit: 25 }))
      if (!Array.isArray(response?.data?.results)) throw new Error("Invalid response")
      return response.data.results.map(account => ({ label: accountName(account), value: { id: String(account.id || account._id), label: accountName(account) } }))
    } catch (error) {
      setFailure("Could not load organizers. Type again to retry.")
      return []
    }
  }
  return <>
    <AsyncSelect inputId={id} classNamePrefix="ep-select" {...portalMenu} loadOptions={load} isDisabled={disabled} isClearable
      value={organizer ? { label: organizer.label || "The organizer chosen", value: organizer } : null} placeholder="Search an organizer"
      onChange={option => onChange(option?.value || null)} noOptionsMessage={() => "Type at least two letters of the organizer's name"} />
    {failure && <small className="text-danger">{failure}</small>}
  </>
}
// The organizer: one of the club's activated organizers for a club of the platform, any activated organizer for TBA or No club
// (ticket 08; checked when the proposal is sent).
function OrganizerField({ id, club, organizer, disabled, onChange }) {
  const clubId = club?.kind === "club" ? club.id : null
  const [state, setState] = useState({})
  useEffect(() => {
    if (!clubId) return undefined
    let current = true
    setState({ loading: true })
    get("/clubs/" + clubId + "/organizers?status=activated&limit=100&page=1")
      .then(response => current && setState({ organizers: (response?.data?.results || []).filter(account => account.status === "activated")
        .map(account => ({ id: String(account.id || account._id), label: accountName(account) })) }))
      .catch(() => current && setState({ error: "Could not load this club's organizers." }))
    return () => { current = false }
  }, [clubId])
  if (clubId) {
    const options = [...(organizer && !(state.organizers || []).some(({ id: known }) => known === organizer.id)
      ? [{ id: organizer.id, label: organizer.label || "The organizer chosen" }] : []), ...(state.organizers || [])]
    return <>
      <select id={id} className="form-control" value={organizer?.id || ""} disabled={disabled}
        onChange={event => onChange(options.find(option => option.id === event.target.value) || null)}>
        <option value="">No organizer yet</option>
        {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
      </select>
      {state.loading && <small className="ep-meta">Loading the club's organizers…</small>}
      {state.error && <small className="text-danger">{state.error}</small>}
      {state.organizers && !state.organizers.length && <small className="ep-meta">This club has no activated organizer yet.</small>}
    </>
  }
  return <OrganizerSearch id={id} organizer={organizer} disabled={disabled} onChange={onChange} />
}

const options = (list, empty) => [<option key="" value="">{empty}</option>,
  ...(list || []).map(item => <option key={String(item.key ?? item.name)} value={String(item.key ?? item.name)}>{item.name || String(item.key)}</option>)]
const ENTRIES = [{ key: 1, name: "1" }, { key: 2, name: "2" }, { key: 3, name: "3" }]

// The Event tab (ticket 15 canvas): identity, dates and place, courts and balls, people. Texts, images and the logistics suppliers
// are not edited in the Events Planner yet; publishing fills them (Phase E1p).
export default function ProposalEventFields({ values, errors, settings, sourceOf, disabled, set }) {
  const country = clubCountry(values.club) || values.country
  const timeZone = countryTimezone(country) || "Europe/Bucharest"
  const input = (name, extra = {}) => ({ id: "proposal-" + name, name, className: "form-control", value: values[name], disabled,
    onChange: event => set({ [name]: event.target.value }), "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `proposal-${name}-error` : undefined, ...extra })
  const oneDay = values.startDay && values.startDay === values.endDay
  const timesHelp = values.startDay ? (oneDay ? `From ${values.startTime} to ${values.endTime}` : `From ${values.startTime} on the first day to ${values.endTime} on the last`)
    + `, ${timeZone} time. Season: ${seasonOfDay(values.startDay) === "summer" ? "Summer" : "Winter"}.` : undefined
  return <div className="ep-form">
    <h3>Identity</h3>
    <div className="ep-grid">
      <Field id="proposal-name" label="Event name" source={sourceOf("name")} wide>
        {/* "Check the name" (ticket 17) stays until the name is changed or confirmed. */}
        <input {...input("name", { placeholder: "Can be set later, before publishing",
          onChange: event => set({ name: event.target.value, ...(values.checkName && { checkName: false }) }) })} />
        {values.checkName && <span className="ep-check-name">
          <span className="ep-mark ep-mark-soon">Check the name: it repeats a year or an edition number.</span>
          <button type="button" className="btn btn-link btn-sm p-0" disabled={disabled} onClick={() => set({ checkName: false })}>The name is right</button>
        </span>}</Field>
      <Field id="proposal-sportType" label="Sport" required source={sourceOf("sportType")} error={errors.sportType}>
        <select {...input("sportType")}>{options((settings.activeSports || ["tennis", "padel"]).map(key => ({ key, name: key === "padel" ? "Padel" : "Tennis" })), "Choose a sport")}</select></Field>
      <Field id="proposal-circuit" label="Circuit" required>
        <select id="proposal-circuit" className="form-control" defaultValue="sportya" disabled>
          <option value="sportya">Sportya</option><option value="tenis10" disabled>Tenis10 (after the Tenis10 release)</option>
        </select></Field>
      <Field id="proposal-category" label="Category" required source={sourceOf("category")} error={errors.category}>
        <select {...input("category")}>{options(settings.categories, "Choose a category")}</select></Field>
      <Field id="proposal-playMode" label="Play mode" source={sourceOf("playMode")}>
        <select {...input("playMode")}>{options(settings.playModes || [{ key: "weekend", name: "Weekend" }, { key: "weekdays", name: "Weekdays" }], "Not chosen yet")}</select></Field>
    </div>
    <h3>Dates and place</h3>
    <div className="ep-grid">
      <Field id="proposal-startDay" label="Start date" required source={sourceOf("startDate")} error={errors.startDay}>
        <DateTimeField {...input("startDay")} mode="date" rangeEnd={values.endDay || ""} /></Field>
      <Field id="proposal-endDay" label="End date" required source={sourceOf("endDate")} error={errors.endDay} help={timesHelp}>
        <DateTimeField {...input("endDay")} mode="date" rangeStart={values.startDay || ""} /></Field>
      <Field id="proposal-club" label="Club" source={sourceOf("club")} wide>
        <ClubField id="proposal-club" club={values.club} disabled={disabled}
          onChange={club => set({ club, organizer: null, ...(clubCountry(club) ? { country: clubCountry(club) } : {}) })} /></Field>
      {!clubCountry(values.club) && <Field id="proposal-country" label="Country" required error={errors.country}>
        <select {...input("country")}>{options(COUNTRIES.map(name => ({ key: name, name })), "Choose the country")}</select></Field>}
      <Field id="proposal-organizer" label="Organizer" source={sourceOf("organizer")} wide
        help={values.club?.kind === "club" ? "One of the club's activated organizers." : "Any activated organizer, for TBA or No club."}>
        <OrganizerField id="proposal-organizer" club={values.club} organizer={values.organizer} disabled={disabled} onChange={organizer => set({ organizer })} /></Field>
    </div>
    <h3>Courts and balls</h3>
    <div className="ep-grid">
      <Field id="proposal-courtType" label="Court surface" source={sourceOf("courtType")}>
        <select {...input("courtType")}>{options(settings.courtSurfaces, "Not chosen yet")}</select></Field>
      <Field id="proposal-numberOfCourts" label="Number of courts" source={sourceOf("numberOfCourts")} error={errors.numberOfCourts}>
        <input {...input("numberOfCourts", { type: "number", min: 0, step: 1 })} /></Field>
      <Field id="proposal-officialBall" label="Official ball" source={sourceOf("officialBall")}>
        <select {...input("officialBall")}>
          <option value="">Not chosen yet</option>
          {(settings.balls || []).map(ball => <option key={ball.name} value={ball.name}>{ball.name}</option>)}
          {values.officialBall && values.officialBall !== BALLS_TBD && !(settings.balls || []).some(ball => ball.name === values.officialBall)
            && <option value={values.officialBall}>{values.officialBall}</option>}
          <option value={BALLS_TBD}>Balls TBD</option>
        </select></Field>
    </div>
    <h3>People and entries</h3>
    <div className="ep-grid">
      <Field id="proposal-coordinator" label="Coordinator" source={sourceOf("coordinator")}>
        <select {...input("coordinator")}>{options((settings.coordinators || []).map(({ name }) => ({ key: name, name })), "Not chosen yet")}
          {values.coordinator && !(settings.coordinators || []).some(({ name }) => name === values.coordinator) && <option value={values.coordinator}>{values.coordinator}</option>}
        </select></Field>
      <Field id="proposal-directorName" label="Tournament director" source={sourceOf("directorName")}><input {...input("directorName")} /></Field>
      <Field id="proposal-refereeName" label="Referee" source={sourceOf("refereeName")}><input {...input("refereeName")} placeholder="Optional" /></Field>
      <Field id="proposal-supervisorName" label="Supervisor" source={sourceOf("supervisorName")}><input {...input("supervisorName")} placeholder="Optional" /></Field>
      <Field id="proposal-singlesMaxNumber" label="Singles entries per player" source={sourceOf("singlesMaxNumber")}>
        <select {...input("singlesMaxNumber")}>{options(ENTRIES, "Not chosen yet")}</select></Field>
      <Field id="proposal-doublesMaxNumber" label="Doubles entries per player" source={sourceOf("doublesMaxNumber")}>
        <select {...input("doublesMaxNumber")}>{options(ENTRIES, "Not chosen yet")}</select></Field>
    </div>
  </div>
}
